import * as React from 'react';
import { useFormControlContext, useStyles, error, isNumber, isUndefined } from '@sravni/react-utils';
import { getCurrentStep } from '../../../helpers/getCurrentStep.js';
import modules_95586cb6 from '../styles/NumberInput.module.less.js';
import { NumberInputView } from './NumberInputView.js';

const rangeValue = (value, max, min) => {
    if (max === undefined && min === undefined) {
        return Number(value);
    }
    if (max === undefined && min !== undefined) {
        return Math.max(value, min);
    }
    if (max !== undefined && min === undefined) {
        return Math.min(value, max);
    }
    return Math.min(max, Math.max(value, min));
};
const roundToNearestStep = (value, step) => {
    const currentStep = getCurrentStep(step, value);
    if (!currentStep) {
        return value;
    }
    if (currentStep < 1) {
        const decimalPart = value - Math.floor(value);
        const integerStep = 1 / currentStep;
        const roundedDecimalPart = Math.round(decimalPart * integerStep) / integerStep;
        return Math.floor(value) + roundedDecimalPart;
    }
    return Math.round(value / currentStep) * currentStep;
};
/**
 * @description Enter a number within certain range with the mouse or keyboard
 *
 * @component
 * @example
 * ```jsx
 * <NumberInput
 *    label="Название поля"
 *    min={30000}
 *    max={1000000}
 *    postfix="₽/мес"
 *    thousandSeparator=" "
 *    decimalScale={2}
 *  />
 * ```
 */
const NumberInput = React.forwardRef((props, ref) => {
    const { value, min, max, step, decimalScale, fixedDecimalScale, decimalSeparator, thousandSeparator, invalid, disabled, onChange, onBlur, onKeyPress, format, defaultValue, className, ...restProps } = props;
    const formControlContext = useFormControlContext();
    const cx = useStyles(modules_95586cb6);
    const classNames = cx(className, 'container');
    const condition = decimalSeparator === thousandSeparator;
    // Ensure the decimalSeparator is not equal the thousandSeparator
    if (condition) {
        error({
            condition,
            message: 'Ensure the decimalSeparator is not equal the thousandSeparator',
        })();
    }
    const empty = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const prepareValue = (value) => {
        if (value === undefined || value === empty) {
            return undefined;
        }
        return step ? roundToNearestStep(rangeValue(value, max, min), step) : rangeValue(value, max, min);
    };
    const [innerValue, setInnerValue] = React.useState();
    const isFirstRender = React.useRef(true);
    const handleValueChange = (values, sourceInfo) => {
        if (!sourceInfo.event || values.floatValue === innerValue) {
            return;
        }
        setInnerValue(values.floatValue);
        onChange?.(values.floatValue);
    };
    const setPreparedValue = () => {
        setInnerValue((value) => {
            const preparedValue = prepareValue(value);
            if (value !== preparedValue && value !== empty) {
                onChange?.(preparedValue);
            }
            return preparedValue;
        });
    };
    const handleBlur = (event) => {
        setPreparedValue();
        onBlur?.(event);
    };
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            setPreparedValue();
        }
        onKeyPress?.(event);
    };
    React.useEffect(() => {
        if (!isFirstRender.current) {
            isNumber(value) && setInnerValue(value);
            if (isUndefined(value) || value === '') {
                setInnerValue('');
            }
        }
        else {
            setInnerValue(prepareValue(value ?? defaultValue));
            isFirstRender.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return (React.createElement("div", { "data-qa": "NumberInput", className: classNames },
        React.createElement(NumberInputView, { ...restProps, ref: ref, value: innerValue, thousandSeparator: thousandSeparator, decimalSeparator: decimalSeparator, decimalScale: decimalScale, fixedDecimalScale: fixedDecimalScale, format: format, inputMode: "decimal", invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, onValueChange: handleValueChange, onKeyPress: handleKeyPress, onBlur: handleBlur })));
});
NumberInput.defaultProps = {
    decimalSeparator: ',',
    fixedDecimalScale: true,
    allowedDecimalSeparators: [',', '.'],
    allowNegative: false,
};
NumberInput.displayName = 'NumberInput';

export { NumberInput };
//# sourceMappingURL=NumberInput.js.map
